/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * A shared list, for someone outside the family (docs/SHOPPING_LIST_10X.md
 * §11 decision 5) — Grandma, opening a link on her phone.
 *
 * No account and no app chrome: just the one list, live. She can add things,
 * tick them off and fix what she added; everything she does shows as
 * "Grandma (via link)" for the family. When the link runs out or the family
 * switches it off, the page says so plainly.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  followStream, guestFetch, type GuestState, type LiveChange, type LiveItem, type OpResult,
} from "../../api/shoppingLive.ts";
import { Icon } from "../../components/Icon.tsx";

type Status = "loading" | "live" | "connecting" | "gone" | "error";

export function GuestListView({ token }: { token: string }) {
  const [state, setState] = useState<GuestState | null>(null);
  const [items, setItems] = useState<Record<string, LiveItem>>({});
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string | null>(null);
  const [text, setText] = useState("");
  const seq = useRef(0);

  const load = useCallback(async () => {
    try {
      const s = await guestFetch<GuestState>(token);
      setState(s);
      setItems(Object.fromEntries(s.items.map((i) => [i.id, { ...i, quantity: i.quantity === null ? null : Number(i.quantity) }])));
      seq.current = s.latestSeq;
      document.title = `${s.list.title} — shared list`;
      return true;
    } catch (err) {
      const status = (err as { status?: number }).status;
      setStatus(status === 404 ? "gone" : "error");
      setMessage((err as Error).message);
      return false;
    }
  }, [token]);

  // Load, then follow the list live; reconnect with a gentle backoff.
  useEffect(() => {
    let stop = false;
    let ac: AbortController | null = null;
    void (async () => {
      if (!(await load())) return;
      let attempt = 0;
      while (!stop) {
        ac = new AbortController();
        let closed = false;
        try {
          await followStream(seq.current, {
            onReady: () => { attempt = 0; setStatus("live"); },
            onChange: (c: LiveChange) => {
              seq.current = Math.max(seq.current, c.seq);
              setItems((prev) => {
                const next = { ...prev };
                if (c.op === "item.upsert" && c.itemId) {
                  const p = c.payload as unknown as LiveItem;
                  next[c.itemId] = { ...p, quantity: p.quantity === null ? null : Number(p.quantity) };
                } else if ((c.op === "item.delete" || c.op === "item.purge") && c.itemId) {
                  delete next[c.itemId];
                }
                return next;
              });
              if (c.op === "list.upsert") {
                const title = (c.payload as { title?: string }).title;
                if (title) setState((s) => (s ? { ...s, list: { ...s.list, title } } : s));
              }
            },
            onClosed: () => { closed = true; },
          }, ac.signal, token);
        } catch {
          /* dropped: reconnect below */
        }
        if (stop) return;
        if (closed) {
          // Revoked or run out: confirm, then say so.
          if (!(await load())) return;
        }
        setStatus("connecting");
        await new Promise((r) => setTimeout(r, [1000, 3000, 10_000, 30_000][Math.min(attempt++, 3)]));
      }
    })();
    return () => { stop = true; ac?.abort(); };
  }, [load, token]);

  const send = async (ops: Array<Record<string, unknown>>) => {
    try {
      const r = await guestFetch<{ results: OpResult[] }>(token, "/ops",
        { ops: ops.map((o) => ({ id: crypto.randomUUID(), clientTs: new Date().toISOString(), ...o })) });
      const bad = r.results.find((x) => x.status === "rejected");
      if (bad) setMessage(bad.error ?? "That didn't work.");
    } catch (err) {
      const s = (err as { status?: number }).status;
      if (s === 404) { setStatus("gone"); setMessage((err as Error).message); }
      else setMessage("No connection — try again in a moment.");
    }
  };

  const order = useMemo(() => new Map((state?.categories ?? []).map((c, i) => [c.key, i])), [state]);
  const needed = Object.values(items).filter((i) => i.status !== "got")
    .sort((a, b) => (order.get(a.aisle ?? "other") ?? 999) - (order.get(b.aisle ?? "other") ?? 999)
      || (a.position ?? 0) - (b.position ?? 0));
  const got = Object.values(items).filter((i) => i.status === "got");

  if (status === "gone") {
    return (
      <Shell>
        <h1 className="type-title">This link has ended</h1>
        <p className="text-ink-2">{message ?? "Ask the family for a new one."}</p>
      </Shell>
    );
  }
  if (!state) {
    return <Shell><p className="text-ink-3">{status === "error" ? message : "Opening the list…"}</p></Shell>;
  }

  const catName = (k: string | null) => state.categories.find((c) => c.key === (k ?? "other"))?.name ?? "";
  const mine = (i: LiveItem) => i.added_by === state.me;

  return (
    <Shell>
      <header className="space-y-1">
        <p className="text-xs text-ink-3">
          {state.sharedBy ? `${state.sharedBy} shared this list with you` : "A list shared with you"} ·
          until {new Date(state.expiresAt).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
        </p>
        <h1 className="type-title">{state.list.icon ? `${state.list.icon} ` : ""}{state.list.title}</h1>
        <p className="flex items-center gap-1.5 text-xs text-ink-3">
          <span className={`h-2 w-2 rounded-full ${status === "live" ? "bg-ok" : "bg-ink-3"}`} />
          {status === "live" ? "Live — the family sees what you do" : "Connecting…"}
        </p>
      </header>

      <form className="flex gap-2" onSubmit={(e) => {
        e.preventDefault();
        const t = text.trim();
        if (!t) return;
        void send([{ op: "item.add", text: t }]);
        setText("");
      }}>
        <input className="input text-base" value={text} placeholder={'Add something — "2 lemons"'}
          aria-label="Add to the list" onChange={(e) => setText(e.target.value)} />
        <button className="btn-primary" disabled={!text.trim()}>Add</button>
      </form>
      {message && <p className="text-sm text-danger">{message}</p>}

      <div className="card divide-y divide-rule-soft">
        {needed.length === 0 && <p className="p-4 text-sm text-ink-3">Nothing left to get.</p>}
        {needed.map((i) => (
          <div key={i.id} className="flex items-center gap-3 px-3 py-2">
            <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border-2 border-rule"
              aria-label={`Tick ${i.display_name}`}
              onClick={() => void send([{ op: "item.status", itemId: i.id, status: "got" }])} />
            <div className="min-w-0 flex-1">
              <div className="text-base">
                {i.display_name}
                {i.quantity !== null && <strong className="ml-1.5">{i.unit && i.unit !== "ct" ? `${i.quantity} ${i.unit}` : `×${i.quantity}`}</strong>}
              </div>
              <div className="text-xs text-ink-3">
                {[catName(i.aisle), i.notes, mine(i) ? "added by you" : null].filter(Boolean).join(" · ")}
              </div>
            </div>
            {mine(i) && (
              <button className="text-ink-3 hover:text-danger" aria-label={`Remove ${i.display_name}`}
                onClick={() => void send([{ op: "item.delete", itemId: i.id }])}>
                <Icon name="x" className="h-5 w-5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {got.length > 0 && (
        <details>
          <summary className="cursor-pointer text-sm text-ink-3">Got ({got.length})</summary>
          <div className="card mt-2 divide-y divide-rule-soft">
            {got.map((i) => (
              <button key={i.id} className="flex w-full items-center gap-3 px-3 py-2 text-left text-ink-3"
                onClick={() => void send([{ op: "item.status", itemId: i.id, status: "needed" }])}>
                <Icon name="check" className="h-5 w-5 text-accent" />
                <span className="line-through">{i.display_name}</span>
              </button>
            ))}
          </div>
        </details>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-paper">
      <main className="mx-auto max-w-lg space-y-4 p-4">{children}</main>
      <p className="pb-6 text-center text-xs text-ink-4">MealPlanner · shared list</p>
    </div>
  );
}
